/* eslint-disable no-restricted-syntax */
import { cloneDeep, get } from 'lodash'

function sort(invoiceLinesByLocation) {
  for (const [_, invoiceLines] of invoiceLinesByLocation) {
    invoiceLines.sort((a, b) => {
      // Protection in case there is no serviceStartDate
      let aDate
      let bDate
      try {
        aDate = new Date(a.serviceStartDate)
      } catch (err) {
        aDate = new Date()
      }
      try {
        bDate = new Date(b.serviceStartDate)
      } catch (err) {
        bDate = new Date()
      }

      // Sort by 1) service type, 2) service start date, 3) service name
      return (
        (a.pricedServiceType || '')?.localeCompare(b.pricedServiceType) ||
        aDate.getTime() - bDate.getTime() ||
        (a.summary || '')?.localeCompare(b.summary)
      )
    })
  }
}

export const getInvoiceLinesByLocation = invoice => {
  const invoiceLinesByLocation = new Map()
  for (const invoiceLine of get(invoice, 'invoiceLines', [])) {
    const { locationId } = invoiceLine

    const invoiceLines = invoiceLinesByLocation.get(locationId)
    if (!invoiceLinesByLocation.has(locationId)) {
      invoiceLinesByLocation.set(locationId, [invoiceLine])
    } else if (
      invoiceLine.pricedServiceType === 'On Request' ||
      !invoiceLines.find(line => line.configuredServiceId === invoiceLine.configuredServiceId && line.serviceStartDate === invoiceLine.serviceStartDate)
    ) {
      invoiceLines.push(invoiceLine)
    }
  }
  sort(invoiceLinesByLocation)
  return invoiceLinesByLocation
}

export const getSubtotalByLocation = invoiceLinesByLocation => {
  const subtotalByLocation = new Map()
  for (const [locationId, invoiceLines] of invoiceLinesByLocation) {
    let subtotal = 0
    for (const invoiceLine of invoiceLines) {
      subtotal += invoiceLine.total
    }
    subtotalByLocation.set(locationId, subtotal)
  }

  return subtotalByLocation
}

/**
 * Organizes invoice line charges by sorting fees and taxes and adding hierarchy information
 * @param {Object} invoiceLine - The invoice line containing charges to organize
 * @returns {Object} The same invoice line with organized charges
 */
export const organizeInvoiceLineCharges = (invoiceLine) => {
  // Pull SERVICE-placed taxes aside before positional inference. A SERVICE-grouped tax
  // is the aggregated tax for the whole service line — it doesn't belong to any one
  // fee, so it must not be indented under one. It renders at hierarchyLevel 0 at the
  // bottom of the sub-charges, as a sibling to the fees.
  const serviceLevelTaxes = invoiceLine.invoiceFeeTaxCharges.filter(
    charge => charge.chargeType === 'Tax' && charge.placementLevel === 'SERVICE'
  )
  const positionalCharges = invoiceLine.invoiceFeeTaxCharges.filter(
    charge => !(charge.chargeType === 'Tax' && charge.placementLevel === 'SERVICE')
  )

  // Find indices of all fees in the positional subset
  const feeIndices = []
  positionalCharges.forEach((charge, index) => {
    if (charge.chargeType !== 'Tax') {
      feeIndices.push(index)
    }
  })

  // Extract all fees (at the indices we found)
  const fees = feeIndices.map(index => positionalCharges[index])

  // Add the last index for sliding window bounds
  feeIndices.push(positionalCharges.length)

  // Extract service-line LINE_ITEM taxes (all taxes before the first fee)
  let serviceTaxes = positionalCharges
    .slice(0, feeIndices[0])
    .filter(charge => charge.chargeType === 'Tax')

  // Create a map of fees to their associated taxes
  const feeMap = new Map()

  // For each fee, associate the taxes that follow it (before the next fee)
  for (let i = 0; i < feeIndices.length - 1; i++) {
    const [start, end] = [feeIndices[i], feeIndices[i + 1]]
    const fee = positionalCharges[start]

    const feeTaxes = positionalCharges
      .slice(start + 1, end)
      .filter(charge => charge.chargeType === 'Tax')

    if (feeTaxes.length > 0) {
      feeMap.set(fee, feeTaxes)
    }
  }

  // Sort service taxes by amount decreasing
  serviceTaxes.sort((a, b) => (b.amount || 0) - (a.amount || 0))

  // Sort SERVICE-grouped taxes by amount decreasing
  serviceLevelTaxes.sort((a, b) => (b.amount || 0) - (a.amount || 0))

  // Sort fees by date increasing
  fees.sort((a, b) => {
    if (!a.createdAt) return -1
    if (!b.createdAt) return 1
    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  })

  // Sort fee taxes by amount decreasing
  feeMap.forEach(taxes => {
    taxes.sort((a, b) => (b.amount || 0) - (a.amount || 0))
  })

  // Combine all in the required order and add hierarchy information
  const orderedCharges = []

  // Add service-line LINE_ITEM taxes first (head of the sub-charges)
  serviceTaxes.forEach(tax => {
    tax.hierarchyLevel = 0 // Root level
    orderedCharges.push(tax)
  })

  // Add fees with their associated LINE_ITEM taxes
  fees.forEach(fee => {
    fee.hierarchyLevel = 0 // Root level
    orderedCharges.push(fee)

    // Add fee's taxes if any
    const feeTaxes = feeMap.get(fee) || []
    feeTaxes.forEach(tax => {
      tax.hierarchyLevel = 1 // Indented level
      orderedCharges.push(tax)
    })
  })

  // Add SERVICE-grouped taxes at the bottom, as siblings to the fees
  serviceLevelTaxes.forEach(tax => {
    tax.hierarchyLevel = 0 // Root level
    orderedCharges.push(tax)
  })

  // Replace the original array with our ordered one
  invoiceLine.invoiceFeeTaxCharges = orderedCharges

  return invoiceLine
}

export const transformInvoiceLineByLocation = invoiceLinesByLocation => {
  const invoiceLinesByLocationClone = cloneDeep(invoiceLinesByLocation)

  invoiceLinesByLocationClone.forEach(invoiceLines => {
    ;(invoiceLines || []).forEach(invoiceLine => {
      if (invoiceLine.invoiceFeeTaxCharges && invoiceLine.invoiceFeeTaxCharges.length > 0) {
        // First, set amount property on all charges for sorting
        invoiceLine.invoiceFeeTaxCharges.forEach(charge => {
          if (invoiceLine.pricedServiceType === 'On Request') {
            charge.createdAt = ''
          }

          // Taxes should not have date
          if (charge.chargeType === 'Tax') {
            charge.createdAt = ''
          }
          
          // Set the amount property for sorting
          charge.amount = charge.feeChargeCents || (charge.feeCharge * 100)
        })

        // Use the new function to organize the charges
        organizeInvoiceLineCharges(invoiceLine)
      }
    })
  })

  return invoiceLinesByLocationClone
}

export const getVacationHoldData = vacationHolds => {
  const vacationHoldsById = new Map()

  for (const vacationHold of vacationHolds) {
    const { locationIds } = vacationHold
    for (const locationId of locationIds) {
      if (!vacationHoldsById.has(locationId)) {
        vacationHoldsById.set(locationId, [vacationHold])
      } else {
        const existingVacationHolds = vacationHoldsById.get(locationId)
        if (Array.isArray(existingVacationHolds)) {
          vacationHoldsById.set(locationId, [...existingVacationHolds, vacationHold])
        }
      }
    }
  }
  for (const [locationId] of vacationHoldsById) {
    const existingVacationHolds = vacationHoldsById.get(locationId)
    if (Array.isArray(existingVacationHolds)) {
      vacationHoldsById.set(
        locationId,
        existingVacationHolds.sort((a, b) => {
          const dateATime = new Date(a.startDate).getTime()
          const dateBTime = new Date(b.startDate).getTime()
          return dateATime - dateBTime
        })
      )
    }
  }
  return vacationHoldsById
}
